import React, { useState, useRef, useEffect, useMemo } from 'react'

import Style from './ThreatFeedDetections.module.css'

// ===========================================================================
// Reusable select (PIXMC-6500).
//
// One component for every Threat Feed filter — pass `options` ({ value, name, id? }),
// `value`, and `onChange(value)`. It renders a mockup-styled trigger + a dropdown list.
//   - `searchable` (default true): shows a search box. Used for User and Brand.
//   - `searchable={false}`: no search box — used for Date range and Status.
//   - `onSearch(query)`: when provided, searching is SERVER-SIDE — the parent loads the
//     options (e.g. extension-user search); the component shows `options` as-is instead of
//     filtering locally. When absent, the list is filtered locally by `name`.
// Closes on outside click.
// ===========================================================================

const ThreatFeedSearchSelect = ({ options, value, onChange, ariaLabel, searchable = true, onSearch }) => {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const ref = useRef(null)
  const timer = useRef(null)

  useEffect(() => {
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false)
    }
    document.addEventListener('mousedown', onDoc)
    return () => document.removeEventListener('mousedown', onDoc)
  }, [])

  const list = options || []
  const selected = list.find((o) => o.value === value) || list[0]

  const shown = useMemo(() => {
    // Server-side search (onSearch) → parent already supplies the filtered list.
    if (onSearch) return list
    const q = query.trim().toLowerCase()
    if (!q) return list
    return list.filter((o) => (o.name || '').toLowerCase().includes(q))
  }, [list, query, onSearch])

  const handleQuery = (e) => {
    const q = e.target.value
    setQuery(q)
    if (onSearch) {
      if (timer.current) clearTimeout(timer.current)
      timer.current = setTimeout(() => onSearch(q), 300)
    }
  }

  const pick = (o) => {
    onChange(o.value)
    setOpen(false)
    setQuery('')
  }

  return (
    <div className={Style.searchSelect} ref={ref}>
      <button
        type="button"
        className={Style.searchSelectTrigger}
        onClick={() => setOpen((o) => !o)}
        aria-label={ariaLabel}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className={Style.searchSelectValue}>{selected ? selected.name : ''}</span>
        <span className={Style.searchSelectCaret}>▾</span>
      </button>

      {open && (
        <div className={Style.searchSelectPanel}>
          {searchable && (
            <input
              className={Style.searchSelectInput}
              type="text"
              value={query}
              onChange={handleQuery}
              placeholder="Search…"
              aria-label={`${ariaLabel} search`}
              autoFocus
            />
          )}
          <div className={Style.searchSelectList} role="listbox">
            {shown.length === 0 ? (
              <div className={Style.searchSelectEmpty}>No matches</div>
            ) : (
              shown.map((o) => (
                <button
                  type="button"
                  key={o.id != null ? o.id : o.value}
                  className={`${Style.searchSelectItem} ${o.value === value ? Style.searchSelectItemActive : ''}`}
                  onClick={() => pick(o)}
                  role="option"
                  aria-selected={o.value === value}
                >
                  {o.name}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export default ThreatFeedSearchSelect
